import type { MouseEvent } from 'react';
import React, { memo } from 'react';
import styled from '@emotion/styled';
import type { SizeProps, SpaceProps } from 'styled-system';
import { color, size, space } from 'styled-system';

import icons from '../assets/icons';
import type { ColorProps, FillProps, StrokeProps } from '../theme/system-types';
import { fill, stroke } from '../theme/system-utilities';

export const DEFAULT_ICON_SIZE = [24, null, null, 32];

export type IconName = keyof typeof icons;

export enum Rotate {
  Left = 'rotate(0)',
  Right = 'rotate(180deg)',
  Up = 'rotate(90deg)',
  Down = 'rotate(-90deg)',
}

export interface BaseIconProps extends SizeProps, FillProps, StrokeProps, ColorProps {
  className?: string;
  dataTestId?: string;
  role?: string;
  title?: string;
  ariaLabel?: string;
  rotate?: string;
  id?: string;
  onClick?: (event: MouseEvent<SVGSVGElement>) => void;
  styles?: SpaceProps;
}

export interface IconPropsWithName extends BaseIconProps {
  name: IconName;
}

export interface IconPropsWithIcon extends BaseIconProps {
  icon: BrowserSpriteSymbol;
}

export type IconProps = IconPropsWithName | IconPropsWithIcon;

const Svg = styled.svg<FillProps & StrokeProps & SizeProps>`
  ${fill};
  ${stroke};
  ${size};
  ${color};
  ${space};
`;

/**
 * In order to add the new icon into the sprite
 * please add icons to common/assets/icons/index.ts
 */
const Icon = (props: IconProps) => {
  const {
    className,
    dataTestId = 'icon',
    role,
    title,
    ariaLabel,
    rotate,
    color: colorProp,
    size: sizeProp,
    fill: fillProp = 'sys.neutral.icon.default',
    stroke: strokeProp,
    id: idProp,
    onClick,
    styles: spaceProps,
  } = props;

  const { id, viewBox, href } = 'icon' in props ? props.icon : icons[props.name];
  const iconHref = href ?? `#${id}`;

  return (
    <Svg
      viewBox={viewBox}
      className={className}
      data-testid={dataTestId}
      role={role}
      fill={fillProp}
      stroke={strokeProp}
      size={sizeProp}
      {...(idProp ? { id: idProp } : {})}
      {...(colorProp ? { color: colorProp } : {})}
      {...(onClick ? { onClick } : {})}
      {...(rotate ? { style: { transform: rotate } } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-hidden': true })}
      {...spaceProps}
    >
      {title && <title>{title}</title>}
      <use href={iconHref} />
    </Svg>
  );
};

export default memo(Icon);
